import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { useCartStore } from "../../lib/cartStore";
import { queryKeys } from "../../lib/queryKeys";
import { EmptyState } from "./EmptyState";
import { ExecutionBlock } from "./ExecutionBlock";
import { IntentRow } from "./IntentRow";
import { SimulationBlock } from "./SimulationBlock";

/**
 * Right-side "Transactions plan" panel. Aggregates user-driven intents
 * (withdraw from a position, send-rebalance-to-cart) into one bundle
 * the worker can simulate + broadcast. Mirrors the e-commerce cart
 * pattern: pile up the things you want to do, then Simulate, then
 * Execute. Lives in the dashboard's right column and starts vertically
 * aligned with the first row of cards (Portfolio + Markets).
 */
export function CartPanel() {
	const intents = useCartStore((s) => s.intents);
	const remove = useCartStore((s) => s.remove);
	const clear = useCartStore((s) => s.clear);
	const simulate = useCartStore((s) => s.simulate);
	const execute = useCartStore((s) => s.execute);
	const simulationData = useCartStore((s) => s.simulationData);
	const simulationPending = useCartStore((s) => s.simulationPending);
	const simulationError = useCartStore((s) => s.simulationError);
	const executionData = useCartStore((s) => s.executionData);
	const executionPending = useCartStore((s) => s.executionPending);
	const executionError = useCartStore((s) => s.executionError);

	// When a broadcast lands successfully, balances change on chain —
	// refresh the WalletBalanceCard + Portfolio snapshot so the dashboard
	// reflects post-execute state.
	const queryClient = useQueryClient();
	useEffect(() => {
		if (executionData?.execution.ok) {
			queryClient.invalidateQueries({ queryKey: queryKeys.walletBalances });
			queryClient.invalidateQueries({ queryKey: queryKeys.portfolioSnapshot });
		}
	}, [executionData, queryClient]);

	const empty = intents.length === 0;
	const canExecute = simulationData?.simulation.ok === true;

	return (
		<aside className="sticky top-6 space-y-3 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
			<header className="flex items-center justify-between">
				<div>
					<h2 className="text-base font-semibold text-slate-100">
						Transactions plan
						{!empty && (
							<span className="ml-2 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
								{intents.length}
							</span>
						)}
					</h2>
					<p className="text-[11px] text-slate-500">
						Pending transactions to bundle
					</p>
				</div>
				{!empty && (
					<button
						type="button"
						onClick={() => clear()}
						className="text-[11px] text-slate-400 hover:text-rose-300"
					>
						clear
					</button>
				)}
			</header>

			{empty ? (
				<EmptyState />
			) : (
				<>
					<ul className="space-y-2">
						{intents.map((intent) => (
							<li key={intent.id}>
								<IntentRow intent={intent} onRemove={() => remove(intent.id)} />
							</li>
						))}
					</ul>

					<button
						type="button"
						onClick={simulate}
						disabled={simulationPending}
						className="w-full rounded-md border border-emerald-700/60 px-3 py-2 text-xs font-medium uppercase tracking-wide text-emerald-300 transition hover:border-emerald-500 hover:text-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{simulationPending
							? "Simulating…"
							: simulationData
								? "Re-simulate"
								: "Simulate"}
					</button>

					{simulationError && <ErrorBanner>{simulationError}</ErrorBanner>}

					{simulationData && (
						<SimulationBlock data={simulationData.simulation} />
					)}

					{/* Execute is gated on a successful simulation — you can't
					    sign a bundle the worker hasn't priced + verified. */}
					{canExecute && (
						<button
							type="button"
							onClick={execute}
							disabled={executionPending}
							className="w-full rounded-md bg-emerald-500 px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{executionPending ? "Executing…" : "Execute bundle"}
						</button>
					)}

					{executionError && <ErrorBanner>{executionError}</ErrorBanner>}
					{executionData && <ExecutionBlock data={executionData} />}
				</>
			)}
		</aside>
	);
}
